"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Users,
  Sparkles,
  Crown,
  Heart,
  Building2,
  Target,
  Award,
  ArrowRight,
  Mic,
  BookOpen,
  CheckCircle2,
  Crosshair,
  MessageSquare,
  Shield,
  TrendingUp,
  Briefcase,
  Compass,
  Eye,
  Brain,
  GitBranch,
  Trophy,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const BARRIERS_TREE = [
  {
    id: "structural",
    label: "Structural Barriers",
    body: "The institutional obstacles that have historically excluded women from sovereign-scale leadership — the boardroom composition patterns, the promotion criteria, the succession pipelines.",
    icon: <Building2 className="size-4" />,
    accent: "red" as const,
    children: [
      { id: "structural-1", label: "Boardroom Composition Patterns", body: "The network effects of homogeneous leadership produce the closed loops that exclude qualified candidates from consideration. Sovereign boards reproduce the patterns of the sovereigns who appointed them.", accent: "coral" as const },
      { id: "structural-2", label: "Promotion Criteria Misalignment", body: "When promotion criteria select for confidence rather than competence, the talent architecture systematically overlooks the signals that predict performance. The five operating advantages are misclassified.", accent: "coral" as const },
      { id: "structural-3", label: "Succession Pipeline Distortion", body: "The pipeline that produces tomorrow&apos;s leaders is shaped by today&apos;s selection biases. A pipeline that selects for the wrong signals produces a leadership cohort that is structurally misaligned with operational requirements.", accent: "coral" as const },
    ],
  },
  {
    id: "perceptual",
    label: "Perceptual Barriers",
    body: "The cognitive biases that distort how women&apos;s capabilities are assessed — the same competencies attributed to confidence in men attributed to aggression in women, the same authority attributed to experience in men attributed to overreach in women.",
    icon: <Eye className="size-4" />,
    accent: "purple" as const,
    children: [
      { id: "perceptual-1", label: "Authority Attribution Asymmetry", body: "The same decisive behavior attributed to leadership in male executives is attributed to abrasiveness in female executives. The double bind penalizes women for demonstrating the competencies that leadership demands.", accent: "purple" as const },
      { id: "perceptual-2", label: "Visibility Penalty", body: "The attention economy produces a visibility penalty for women in leadership — higher scrutiny, lower tolerance for error, and amplified consequences for mistakes. Sovereign-scale leadership requires operating under the penalty without allowing it to constrain operational decisions.", accent: "purple" as const },
    ],
  },
  {
    id: "operational",
    label: "Operational Barriers",
    body: "The day-to-day friction that consumes the energy of women leaders — the additional labor of code-switching, the explanatory work of justifying authority, the resistance to unconventional authority structures.",
    icon: <Crosshair className="size-4" />,
    accent: "cyan" as const,
    children: [
      { id: "operational-1", label: "Authority Justification Labor", body: "The energy expended in justifying authority that would be assumed in peers of different demographics. The keynote presents the discipline of redirecting that energy toward operational outcomes rather than toward the validation that the structure will not provide.", accent: "cyan" as const },
      { id: "operational-2", label: "Code-Switching Overhead", body: "The cognitive cost of adapting communication style, presentation mode, and authority expression to match the expectations of rooms that have not calibrated for the operator&apos;s natural mode. Sovereign-grade operations require minimizing this overhead.", accent: "cyan" as const },
    ],
  },
  {
    id: "narrative",
    label: "Narrative Barriers",
    body: "The stories organizations tell about who leads, who fails, and who belongs — the cultural narratives that shape aspiration, performance expectations, and the interpretation of outcomes.",
    icon: <MessageSquare className="size-4" />,
    accent: "emerald" as const,
    children: [
      { id: "narrative-1", label: "Leadership Narrative Defaults", body: "The cultural scripts that define what a leader looks like, sounds like, and behaves like. When the script does not match the operator, the operator is judged against the script rather than against the outcomes. Sovereign-scale leadership requires rewriting the script — through outcomes that the script cannot dismiss.", accent: "emerald" as const },
      { id: "narrative-2", label: "Failure Attribution Patterns", body: "The same failure attributed to systemic causes in male leaders is attributed to individual incompetence in female leaders. The keynote presents the discipline of producing outcomes that are unambiguous — failures attributable to circumstances, successes attributable to capability.", accent: "emerald" as const },
    ],
  },
  {
    id: "solution",
    label: "Operating Proof",
    body: "The response that dissolves every other barrier: sovereign-scale outcomes that cannot be dismissed. The evidence base that produces fifteen out of eighteen political campaigns won, one-hundred-percent client satisfaction, and zero security incidents across fifteen-plus years.",
    icon: <Award className="size-4" />,
    accent: "amber-gold" as const,
    children: [
      { id: "solution-1", label: "Outcomes That Cannot Be Dismissed", body: "The discipline of producing operational evidence so unambiguous that narrative reframing cannot diminish it. When the evidence is sovereign-scale, the narratives that would diminish it lose credibility. The work speaks. The work accumulates. The work becomes undeniable.", accent: "amber-gold" as const },
      { id: "solution-2", label: "Architecture Over Personality", body: "The discipline of building operating systems that produce results through architecture rather than through the personal brand of any individual. The operator becomes replaceable — the system becomes the authority. The barriers dissolve because they target individuals, not the architecture.", accent: "amber-gold" as const },
    ],
  },
];

const SOVEREIGN_STATS = [
  { value: 100, suffix: "%", label: "Client Satisfaction", description: "Across 300+ elite clients", accent: "emerald" as const, icon: <Trophy className="size-5" /> },
  { value: 15, suffix: "/18", label: "Campaign Victories", description: "Eighty-three point three percent", accent: "red" as const, icon: <Target className="size-5" /> },
  { value: 70, suffix: "%+", label: "Women-Led Teams", description: "Majority-female operating model", accent: "coral" as const, icon: <Users className="size-5" /> },
  { value: 0, label: "Security Incidents", description: "15+ years sovereign-grade", accent: "cyan" as const, icon: <Shield className="size-5" /> },
];

const FAQ_ITEMS = [
  { q: "What is \"Women Breaking Barriers\" about?", a: "The signature keynote on the operational reality of leading at sovereign scale as a woman. The argument is that the most effective response to barriers is operational proof — sovereign-scale outcomes that cannot be dismissed. The evidence is a decade of leading teams over seventy percent women across eighteen countries with sovereign-scale results." },
  { q: "How is this different from empowerment keynotes?", a: "Empowerment keynotes argue from moral obligation. This keynote argues from operations. The evidence is not inspirational — it is structural. Fifteen out of eighteen political campaigns won, one-hundred-percent client satisfaction, zero security incidents. The argument is what sovereign-scale women leaders have always known: results dissolve barriers." },
  { q: "What are the four barriers the keynote addresses?", a: "Structural — the institutional obstacles that exclude women from sovereign-scale leadership. Perceptual — the cognitive biases that distort how women&apos;s capabilities are assessed. Operational — the day-to-day friction that consumes the energy of women leaders. Narrative — the cultural stories organizations tell about who leads and who belongs. Each barrier is addressed through the discipline of operating proof." },
  { q: "How long is the standard keynote?", a: "Forty to fifty-five minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Workshop runs two to three hours. Executive briefing runs sixty to ninety minutes. All formats are available virtually and in person." },
  { q: "What audiences benefit most from this keynote?", a: "Women&apos;s leadership summits, corporate women&apos;s networks, government workforce forums, healthcare leadership audiences, technology organizations, executive education programs, and any audience that has been told that women in leadership is a moral question rather than an operating question." },
  { q: "Does the keynote address intersectionality?", a: "The keynote addresses the compounded barriers that women in leadership face across structural, perceptual, operational, and narrative dimensions. The discipline of operating proof — producing sovereign-scale outcomes that cannot be dismissed — is universal in its application. The barriers are specific to context; the response is structural." },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const THREE_PRINCIPLES = [
  {
    num: "01",
    icon: Crosshair,
    title: "Operational Discipline Over Charisma",
    body: "Charisma is the conventional currency of leadership — the ability to inspire, to persuade, to command attention through personal magnetism. In sovereign-scale operations, charisma is unreliable. It depends on the leader&apos;s presence, energy, and mood. Operational discipline is the alternative — the systems, protocols, and accountability mechanisms that produce outcomes regardless of who is in the room.",
    accent: "coral",
  },
  {
    num: "02",
    icon: Building2,
    title: "Systems Over Heroics",
    body: "The individual hero who breaks barriers creates an opening. The system that sustains the opening creates a legacy. The architectural approach to leadership designs communication protocols, collaboration structures, accountability mechanisms, and hiring methodologies that produce outcomes without depending on individual heroism.",
    accent: "purple",
  },
  {
    num: "03",
    icon: Target,
    title: "Results Over Visibility",
    body: "The attention economy rewards visibility — the leader who is seen, heard, and recognized. Sovereign-scale operations reward results — the leader who delivers outcomes regardless of whether those outcomes are visible. The discipline of measuring by outcomes rather than recognition has sustained leadership across eighteen countries and over three hundred elite clients.",
    accent: "cyan",
  },
];

const SEVEN_STRATEGIES = [
  { title: "Lead with Evidence, Not Appeal", body: "The most powerful argument for women in leadership is not a moral one — it is an operational one. Build and present the evidence base that makes leadership self-evident: the outcomes produced, the teams built, the results that speak louder than any argument for inclusion.", accent: "coral" },
  { title: "Prepare the Room Before Entering It", body: "The physician&apos;s diagnostic method applies directly. Before entering a room where credibility must be established, conduct intelligence gathering — understand stakeholders, map power dynamics, identify decision-making processes, and frame outcomes before the discussion begins.", accent: "purple" },
  { title: "Build the System, Not the Heroics", body: "Sustainable leadership is built on systems, not on individual heroism. Design communication protocols, collaboration structures, and accountability mechanisms that produce outcomes regardless of who is in the room — systems that outlast any individual.", accent: "emerald" },
  { title: "Let the Outcomes Speak", body: "In sovereign-scale operations, the outcome is measured — not the effort, not the intention, not the process. Direct energy toward outcomes that command recognition rather than toward visibility that demands attention. Visibility is granted by others. Outcomes are produced by the leader.", accent: "amber-gold" },
  { title: "Build Teams on Performance Signals", body: "The selection process determines the composition. Identify the signals that predict team performance — communication discipline, risk awareness, collaborative depth, retention stability, ego management — and design hiring processes that evaluate these signals.", accent: "red" },
  { title: "Navigate the Credibility Gap Through Operational Superiority", body: "The credibility gap closes when the outcomes speak. The specific practices that establish operational credibility in rooms where it is not assumed: preparation methodology, communication framework, outcome accountability that produce results that cannot be dismissed.", accent: "cyan" },
  { title: "Measure What Matters", body: "Establish outcome accountability structures that measure results rather than visibility, outcomes rather than intentions, performance rather than presentation. The measurement discipline transforms the leadership posture from defensive to commanding.", accent: "denim" },
];

const EVIDENCE = [
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "300+ elite clients", accent: "coral" },
  { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "15 victories / 18", accent: "purple" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 sec max/year", accent: "cyan" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years sovereign", accent: "emerald" },
];

const TAKEAWAYS = [
  { title: "The barriers are real, but the response must be operational.", body: "The most powerful response to exclusion is not complaint. It is proof. Redirect energy from arguing for inclusion to producing outcomes that make inclusion self-evident.", accent: "coral" },
  { title: "The 70% Model is evidence, not aspiration.", body: "Teams built over seventy percent women outperform on five measurable dimensions. The evidence is drawn from fifteen-plus years of sovereign-scale operations. The implementation is the question — and the keynote provides the framework.", accent: "purple" },
  { title: "The diagnostic method is the leadership method.", body: "Before entering any room, before presenting any proposal, before engaging any stakeholder — diagnose first. The physician&apos;s discipline of understanding the situation before prescribing the solution is the most transferable leadership skill available.", accent: "emerald" },
  { title: "Systems outlast heroics.", body: "The individual hero who breaks barriers creates an opening. The system that sustains the opening creates a legacy. Build the communication protocols, collaboration structures, and accountability mechanisms that create conditions for women to lead.", accent: "amber-gold" },
  { title: "Measure outcomes, not visibility.", body: "The most dangerous metric for women in leadership is visibility. Redirect the measurement from visibility to outcomes: the results produced, the teams built, the systems sustained, and the organizational capability created.", accent: "cyan" },
];

const AUDIENCE = [
  { icon: Users, title: "Women&apos;s Leadership Conferences", body: "Women leaders, aspiring leaders, and organizations committed to advancing women — seeking a speaker who has built, not just advocated.", accent: "coral" },
  { icon: Building2, title: "Corporate Diversity Events", body: "Organizations implementing diversity strategies that have produced mixed results — and that recognize the conventional approach has not produced the outcomes promised.", accent: "purple" },
  { icon: Crown, title: "Executive Women&apos;s Forums", body: "Senior women leaders navigating the specific challenges of operating at sovereign scale — the credibility gap, the communication mismatch, the visibility trap.", accent: "emerald" },
  { icon: TrendingUp, title: "Technology Summits", body: "Events addressing the gender gap in technology leadership — where the evidence of majority-female teams delivering sovereign-scale outcomes provides proof and framework.", accent: "cyan" },
  { icon: BookOpen, title: "University Programs", body: "Academic institutions seeking to expose students and administrators to the operational reality of women leading at sovereign scale. The clinical method is teachable and scalable.", accent: "amber-gold" },
  { icon: Briefcase, title: "Government Workforce Summits", body: "Government leaders responsible for institutional diversity, public sector leadership, and workforce development requiring sovereign-scale evidence.", accent: "denim" },
];

const FORMATS = [
  { label: "Standard Keynote", duration: "40–55 min", body: "Structured presentation from core argument through the 70% Model and the seven strategies to the evidence base and closing takeaways.", accent: "coral" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational exploration of the keynote&apos;s themes, guided by a moderator. Effective for women&apos;s leadership gatherings and executive forums.", accent: "purple" },
  { label: "Panel Contribution", duration: "20–30 min", body: "Focused contribution to multi-speaker discussions on women&apos;s leadership, diversity strategy, governance, or technology.", accent: "emerald" },
  { label: "Workshop / Masterclass", duration: "2–3 hrs", body: "Hands-on implementation of the seven strategies. Tangible output: leadership development framework tailored to the organization&apos;s context.", accent: "cyan" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private session applying the seven strategies as diagnostic tools for women&apos;s leadership cohorts and executive teams.", accent: "amber-gold" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "denim" },
];

const GAINS = [
  "An operating argument, not a moral appeal — the evidence that compels strategic action",
  "Seven practical strategies — diagnostic method, preparation framework, system-building, outcome accountability",
  "The 70% Model evidence — measured outcomes that prove majority-female teams outperform",
  "Sovereign-scale credibility — drawn from an operating model verified across eighteen countries",
  "The diagnostic framework — how to diagnose before treating, how to triage under pressure, how to own the outcome",
  "Immediate applicability — every takeaway designed for implementation starting the next day",
];

export default function WomenBreakingBarriersPage() {
  return (
    <>
      <PageHero
        icon={Users}
        overline="Signature Keynote"
        overlineClassName="text-coral"
        title={
          <>
            The Barriers Are Real. <span className="text-gradient-premium">The Response Must Be Operational.</span>
          </>
        }
        subtitle="Women Breaking Barriers is the signature keynote on the operational reality of leading at sovereign scale as a woman. The keynote moves beyond the conventional conversation — it demonstrates the operating method that has overcome barriers through operational superiority, not accommodation. Teams over seventy percent women. Sovereign-grade outcomes. A decade of evidence."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "Women Breaking Barriers" }]}
        badge="The individual operating reality"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">Lessons from a decade of operating at sovereign scale as a woman.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The barriers women face in leadership are real. They are structural, systemic, and persistent. But the most powerful response to those barriers is not rhetoric. It is operational proof — building organizations where women lead, delivering verified outcomes, and letting the results speak with a force that no argument can match.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote moves beyond the conventional conversation about women in leadership. It does not enumerate barriers. It demonstrates the operating method that has overcome them — not through accommodation, but through operational superiority. The evidence is structural: teams built over seventy percent women, operating across eighteen countries, serving the most demanding clients on earth, delivering outcomes that produced a one-hundred-percent client satisfaction rate and an eighty-three-percent political campaign success rate.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Target className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Core Argument</span>
          </div>
          <h2 className="text-display-md mb-4">The barriers are real. The response must be operational.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The central thesis reframes the barriers conversation from grievance to strategy. When women lead and the outcomes speak, the argument for inclusion becomes self-evident — because the evidence compels it.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Three Operating Principles</span>
          </div>
          <h2 className="text-display-md mb-4">Operational discipline, systems thinking, and outcome accountability.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three principles that have enabled Dr. Jyoti Kush to lead at sovereign scale as a woman. Each drawn from verified operational experience and designed for immediate application.
          </p>
        </div>
        <div className="space-y-4 max-w-4xl mx-auto">
          {THREE_PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <p.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Principle {p.num}</span>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Briefcase className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Seven Practical Strategies</span>
          </div>
          <h2 className="text-display-md mb-4">What the audience implements tomorrow.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Seven strategies drawn from the operational reality of leading at sovereign scale as a woman. Grounded in verified outcomes and designed for immediate implementation.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SEVEN_STRATEGIES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five insights for women in leadership.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The minimum viable operating model for women breaking barriers at the highest levels. Specific, implementable actions.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["coral", "purple", "emerald", "amber-gold", "cyan"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`} dangerouslySetInnerHTML={{ __html: t.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: t.body }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote resonates most with women leaders, aspiring leaders, and organizations committed to advancing women into leadership — seeking a speaker who has built, not just advocated.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`} dangerouslySetInnerHTML={{ __html: a.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Briefcase className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Format Options</span>
            </div>
            <h2 className="text-display-md mb-4">Flexibility for every event.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Six delivery formats, each tailored to the audience, the event, and the specific leadership challenges the room faces.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FORMATS.map((f, i) => {
              const a = ACCENT[f.accent];
              return (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                    <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Users className="size-10 text-coral mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Women Breaking Barriers is the individual-level companion to the team-level evidence of The 70% Project. The operating reality of leading at sovereign scale as a woman — and the discipline that makes it possible.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/the-70-percent-project/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Heart className="size-4" />
                The 70% Project Initiative
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-5`}>
            <GitBranch className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Signature Framework</span>
          </div>
          <h2 className="text-display-md mb-4">The Barriers Tree.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The barriers women in sovereign-scale leadership face are not a single obstacle. They are a tree — five roots, each with specific manifestations, each dissolving under the same response: operating proof. The structure of the tree matters. The roots are interconnected. The response — sovereign-scale outcomes that cannot be dismissed — is universal.
          </p>
        </div>
        <TreeBlock entries={BARRIERS_TREE} />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Sovereign-Scale Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">The outcomes that dissolve barriers.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The argument is not rhetorical. It is structural. The evidence is drawn from sovereign-scale operations where the margin for error is zero and the consequences of failure are national.
          </p>
        </div>
        <CounterBlock stats={SOVEREIGN_STATS} columns={4} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Heart className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Problem This Keynote Solves</span>
          </div>
          <h2 className="text-display-md mb-4">The cost of unmeasured potential.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The keynote addresses a failure that is endemic across organizations of every size, industry, and geography: the systematic misallocation of leadership potential based on signals that do not predict performance. The evidence of this failure is everywhere. Leadership teams that do not reflect the talent pool they recruit from. Promotion pipelines that select for confidence rather than competence. Organizational outcomes that lag behind what a fully optimized leadership strategy would produce. The pattern is universal, and the cost is structural.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The root cause is the framework through which leadership is evaluated. The signals that most promotion processes evaluate — self-presentation, assertiveness, visibility, dominance — are signals that select for a particular style of authority. They do not reliably select for the capabilities that produce sovereign-scale outcomes: clinical judgment, diagnostic discipline, cross-domain integration, operational consistency, and outcome accountability. The result is a systematic bias in leadership allocation — toward operators who perform authority rather than operators who produce outcomes.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote solves this by presenting the operating case. The talent pool is misallocated not because of moral failure but because of signal failure. When the signals are corrected — when the selection criteria evaluate the five operating advantages — the resulting leadership teams produce outcomes that the misaligned criteria could not select for. The moral argument fails because it does not change the promotion architecture. The operating argument succeeds because it does. The evidence base is sovereign-scale. The proof is structural. The barriers dissolve under outcomes they cannot dismiss.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "The 70% Project", description: "The team-level companion — the operating evidence for majority-female teams that outperform.", href: "/speaking/the-70-percent-project/", icon: Heart, accent: "emerald" },
          { title: "How Excellence Actually Runs", description: "The operational system that women in leadership execute within — the four-component architecture.", href: "/speaking/how-excellence-actually-runs/", icon: Award, accent: "red" },
          { title: "Leadership as Diagnosis", description: "The clinical skill set that sustains women in rooms where they are not expected.", href: "/speaking/leadership-as-diagnosis/", icon: Brain, accent: "purple" },
          { title: "Build in the Dark", description: "The discipline of measuring by outcomes rather than recognition — the operating reality of women at the top.", href: "/speaking/build-in-the-dark/", icon: Eye, accent: "denim" },
        ]}
      />
    </>
  );
}
